import dayjs from 'dayjs';
import { replaceStr } from './shared';

/**
 * 날짜 범위를 포맷팅하는 함수
 * @param {number} startYear - 시작 연도
 * @param {number} startMonth - 시작 월
 * @param {number} endYear - 종료 연도
 * @param {number} endMonth - 종료 월
 * @returns {string} 포맷팅된 날짜 범위(YYYY.MM ~ YYYY.MM)
 */
export const formatDateRange = (startYear, startMonth, endYear, endMonth) => {
  let start = '';
  let end = '';
  const separator = ' ~ ';

  if (startYear) {
    start = `${startYear}`;
    if (startMonth) {
      start += `.${startMonth.toString().padStart(2, '0')}`;
    }
  }

  if (endYear) {
    end = `${endYear}`;
    if (endMonth) {
      end += `.${endMonth.toString().padStart(2, '0')}`;
    }
  }

  if (startYear || endYear) {
    return start + separator + end;
  }

  return start + end;
};

export const formatDate = (year, month) => {
  let date = '';

  if (year) {
    date = `${year}.`;
  }

  if (month) {
    date += `${month.toString().padStart(2, '0')}`;
  }

  return date;
};

/**
 * 날짜 문자열을 포맷팅하는 함수
 * @param {string} dateString - 날짜 문자열
 * @param {string} format - 포맷
 * @returns {string} 포맷팅된 날짜 문자열
 */
export const formatDateString = (dateString, format = 'YYYY.MM.DD') => {
  const date = dayjs(dateString);

  // 날짜가 유효한지 확인
  if (date.isValid()) {
    // 유효한 경우 포맷팅하여 반환
    return date.format(format);
  } else {
    // 유효하지 않은 경우 원래 문자열 반환
    return dateString;
  }
};

/**
 * @description 전화번호를 받아 - 를 붙여서 반환한다.
 * @param {String} phoneNumber 전화번호
 * @returns {String} 02-123-4567 / 032-123-4567 / 010-1234-5678 ...
 */
export const formatPhoneNumber = (phoneNumber) => {
  try {
    // 숫자만 추출
    const cleaned = replaceStr(phoneNumber);

    // 전화번호 길이에 따라 포맷팅
    let match;
    if (cleaned.length === 8) {
      // 8자리 전화번호 (예: 1234-5678)
      match = cleaned.match(/^(\d{4})(\d{4})$/);
    } else if (cleaned.length === 9 && cleaned.startsWith('02')) {
      // 서울 지역번호 9자리 (예: 02-371-7063)
      match = cleaned.match(/^(\d{2})(\d{3})(\d{4})$/);
    } else if (cleaned.length === 10) {
      if (cleaned.startsWith('02')) {
        // 서울 지역번호 10자리 (예: 02-1234-5678)
        match = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
      } else {
        // 다른 지역번호 10자리 (예: 032-123-4567)
        match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      }
    } else if (cleaned.length === 11) {
      // 11자리 전화번호 (예: 010-1234-5678)
      match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    } else {
      // 그 외의 경우는 포맷팅하지 않음
      return phoneNumber;
    }

    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    return phoneNumber;
  } catch (error) {
    console.error('formatPhoneNumber', error);
  }
};

/**
 * @description 날짜 입력 값을 파싱하는 함수
 * @param {String} dateInput 날짜 입력 값
 * @returns {Object} 파싱된 날짜 객체
 */
export const parseDateInput = (dateInput) => {
  if (!dateInput) return { year: '', month: '' };

  // 점(.)이 포함된 경우 처리
  if (dateInput.includes('.')) {
    const parts = dateInput.split('.');
    const year = parts[0];
    const month = parts[1] || '';

    return { year, month: month.padStart(2, '0') };
  }

  // 숫자만 있는 경우: 길이에 따라 처리
  const length = dateInput.length;

  if (length >= 5) {
    const year = dateInput.substring(0, 4);
    let month = dateInput.substring(4);
    if (month.length === 1) {
      month = month.padStart(2, '0');
    } else if (month.length > 2) {
      month = month.substring(0, 2);
    }
    return { year, month };
  }
  // 4자리 이하는 전부 연도로 취급
  else {
    return { year: dateInput, month: '' };
  }
};

/**
 * @description 숫자를 천 단위로 포맷팅하는 함수
 * @param {Number} num 숫자
 * @returns {String} 천 단위로 포맷팅된 문자열
 */
export const formatAmount = (num) => {
  // 숫자가 아닌 문자열 숫자일 때는 숫자로 변환
  if (typeof num === 'string') {
    num = Number(num);
  }

  // 숫자가 아닌 경우 빈 문자열 반환
  if (isNaN(num)) {
    return '';
  }

  return num.toLocaleString();
};

/**
 * @description 사업자등록번호를 포맷팅하는 함수
 * @param {String} businessNo 사업자등록번호
 * @returns {String} 포맷팅된 사업자등록번호
 */
export const businessNoFormatter = (businessNo) => {
  if (businessNo.length === 10) {
    return businessNo.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
  }
  return businessNo;
};
